/**
 * Gallery-invitation notifications → the app's own invite route.
 *
 * `getNotificationHref` otherwise treats `notification.link` as a frontend path
 * and only locale-prefixes it. That is right for the notification types it was
 * written for, but a gallery invitation is addressed by the *API's* invitation
 * endpoint — `galleries/user-join/{galleryId}/{invite_uuid}` (and its
 * accept counterpart) — which is not a page in this app:
 *
 *   - as a relative link it becomes `/{lang}/galleries/user-join/{id}/{uuid}`,
 *     four segments that match no route (`galleries/[slug]` is one segment);
 *   - as an absolute link it navigates the browser straight at the backend
 *     endpoint, i.e. a plain document GET with no `Authorization: Bearer`
 *     header — which is exactly the unauthenticated request that fails with a
 *     server error instead of rendering an invitation.
 *
 * So the identifiers are lifted out of whatever shape the payload carries and
 * pointed at the real page, `/{lang}/galleries/invite/{galleryId}/{uuid}`, whose
 * client then fetches the invitation through `axiosInstance` (bearer attached)
 * and offers the existing explicit accept action. Nothing is auto-accepted:
 * opening the notification only reads the invitation.
 *
 * Both identifiers must be present, or the link is left to the generic handling
 * rather than guessing a route with a missing segment.
 *
 * Kept import-free (types only) so it is unit-testable directly.
 */

import type { Notification } from "./types";

export type GalleryInviteTarget = {
  galleryId: string;
  inviteUuid: string;
};

/**
 * Matches the invitation-detail endpoint, its accept counterpart, and the
 * frontend route itself — a `link` already pointing at the right page must
 * survive this unchanged. Works on relative and absolute links alike, and
 * tolerates any prefix (`/api/proxy`, a locale segment, a host).
 */
const INVITE_PATH = /galleries\/(?:user-join|accept-user-join|invite)\/([^/?#]+)\/([^/?#]+)/i;

/** Gallery ids are numeric everywhere in this API (`gallery_id`, `/galleries/invite`
 *  bodies), so requiring digits keeps a same-shaped non-invite link from
 *  matching. */
const GALLERY_ID = /^\d+$/;

function firstString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

/** Reads the pair off `meta_data`, which is the payload's own structured copy
 *  and survives a `link` that is missing or points somewhere unusable. */
export function galleryInviteTargetFromMeta(
  metaData: Record<string, any> | null | undefined,
): GalleryInviteTarget | null {
  if (!metaData) return null;
  const invite = (metaData.invite ?? metaData.gallery_invite ?? {}) as Record<string, any>;
  const gallery = (metaData.gallery ?? {}) as Record<string, any>;

  const galleryId = firstString(
    metaData.gallery_id,
    metaData.galleryId,
    invite.gallery_id,
    gallery.id,
  );
  const inviteUuid = firstString(
    metaData.invite_uuid,
    metaData.inviteUuid,
    metaData.uuid,
    invite.uuid,
    invite.invite_uuid,
  );

  if (!GALLERY_ID.test(galleryId) || !inviteUuid) return null;
  return { galleryId, inviteUuid };
}

/** Reads the pair out of an invitation link, relative or absolute. */
export function galleryInviteTargetFromLink(
  link: string | null | undefined,
): GalleryInviteTarget | null {
  if (!link) return null;
  const match = INVITE_PATH.exec(link);
  if (!match) return null;
  const [, galleryId, inviteUuid] = match;
  if (!GALLERY_ID.test(galleryId) || !inviteUuid) return null;
  return { galleryId, inviteUuid };
}

/** Dotted `type` keys are e.g. "messages.follow.gallery_follow.type"; only
 *  invite/join ones may be resolved from `meta_data` alone. */
const INVITE_TYPE = /invite|user[-_]?join/i;

/**
 * The link is tried first (it is the identifier pair the backend actually sent
 * for *this* invitation). `meta_data` is a fallback for an invite notification
 * that carries no usable link, and is gated on the notification `type` so a
 * gallery notification of another kind that happens to carry a `uuid` can't be
 * rerouted into the invite flow.
 */
export function getGalleryInviteTarget(
  notification: Pick<Notification, "link" | "meta_data"> & { type?: string },
): GalleryInviteTarget | null {
  const fromLink = galleryInviteTargetFromLink(notification.link);
  if (fromLink) return fromLink;
  if (!INVITE_TYPE.test(notification.type || "")) return null;
  return galleryInviteTargetFromMeta(notification.meta_data);
}

/** The app's invite page for a target — the one place the route shape lives. */
export function buildGalleryInviteHref(target: GalleryInviteTarget, lang: string): string {
  return `/${lang}/galleries/invite/${encodeURIComponent(target.galleryId)}/${encodeURIComponent(
    target.inviteUuid,
  )}`;
}
